import { useEffect, useState } from "react";
import { FilePdf } from "@phosphor-icons/react";
import api from "../../lib/api";
import { Field, Area, Section, Radio, CheckGroup } from "./fields";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export const TASK_CODES = [
  { code: "102", name: "Bath – Bed" },
  { code: "106", name: "Hair Care – Shampoo" },
  { code: "115", name: "Meal Preparation" }, { code: "116", name: "Housework" },
  { code: "117", name: "Managing Finances" }, { code: "118", name: "Managing Medications" },
  { code: "119", name: "Shopping" }, { code: "120", name: "Transportation" },
  { code: "122", name: "Hygiene" }, { code: "123", name: "Dressing – Upper Body" },
  { code: "124", name: "Dressing – Lower Body" }, { code: "125", name: "Locomotion" },
  { code: "126", name: "Transfers" }, { code: "127", name: "Toilet Use" },
  { code: "128", name: "Bed Mobility" }, { code: "129", name: "Eating" },
  { code: "130", name: "Bladder Incontinence" }, { code: "131", name: "Bowel Incontinence" },
  { code: "132", name: "Other Personal Care" }, { code: "134", name: "Bathing – Wash-Up" },
  { code: "135", name: "Bathing – Shower" }, { code: "137", name: "Lotion" },
  { code: "138", name: "Laundry" }, { code: "139", name: "Reading/Writing" },
  { code: "140", name: "Supervision/Cueing" }, { code: "141", name: "Incontinence Care" }, { code: "145", name: "Stairs" },
];

export const LEVELS = ["Remind", "Coaching/Cueing", "Supervise", "Stand By", "Hands On", "Complete"];
const MOBILITY_CODES = ["125", "126", "128", "145"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const BACKUP_OPTIONS = [
  "Silver may offer another qualified caregiver",
  "Contact client/primary contact before sending replacement",
  "Contact legal representative if required",
  "Client may decline replacement coverage",
];

export const emptyForm4 = {
  effective_date: "", auth_start: "", auth_end: "", authorized_hours: "", hours_period: "",
  supervisor: "", caregivers: "", need: "", goal: "",
  schedule: {}, total_weekly_hours: "", schedule_instructions: "",
  tasks: TASK_CODES.map((t) => ({ ...t, selected: false, levels: [], frequency: "", instruction: "" })),
  med_box_location: "", reminder_times: "", reminder_instructions: "", med_refused_action: "",
  transport_approved: "", transport_vehicle: "", transport_instructions: "",
  equipment_used: "", transfer_instructions: "",
  routines_preferences: "", off_limits: "", safety_instructions: "",
  backup_options: [], backup_instructions: "", verbal_summary: "",
};

const shiftMins = (s, e) => {
  if (!s || !e) return 0;
  const [sh, sm] = s.split(":").map(Number);
  const [eh, em] = e.split(":").map(Number);
  const diff = eh * 60 + em - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
};

const computeTotal = (schedule) => {
  let mins = 0;
  for (const day of DAYS) {
    const d = schedule?.[day];
    mins += shiftMins(d?.start, d?.end) + shiftMins(d?.start2, d?.end2);
  }
  return mins ? (mins / 60).toFixed(1) : "";
};

export default function Form4CarePlan({ data, onChange, clientId, clientCode }) {
  const [safetyFacts, setSafetyFacts] = useState([]);
  const [quick, setQuick] = useState({ start: "08:30", end: "14:00", has2: false, start2: "16:00", end2: "20:00", days: [] });
  const [secondDays, setSecondDays] = useState(new Set());
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  const setR = (k) => (v) => onChange({ ...data, [k]: v });

  const toggleQuickDay = (day) => setQuick((q) => ({ ...q, days: q.days.includes(day) ? q.days.filter((d) => d !== day) : [...q.days, day] }));
  const applyQuick = () => {
    if (!quick.days.length) return;
    const schedule = { ...(data.schedule || {}) };
    for (const day of quick.days) {
      schedule[day] = { start: quick.start, end: quick.end,
        ...(quick.has2 ? { start2: quick.start2, end2: quick.end2 } : {}) };
    }
    onChange({ ...data, schedule, total_weekly_hours: computeTotal(schedule) });
  };

  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}`).then((r) => {
      const p = r.data.forms?.profile || {};
      const facts = [];
      if (p.recent_falls === "Yes") facts.push(`Recent falls: ${p.fall_details || "yes"}`);
      if (p.mobility_status) facts.push(`Mobility: ${p.mobility_status}`);
      if (p.assistive_devices) facts.push(`Assistive devices: ${p.assistive_devices}`);
      if (p.allergies === "Yes") facts.push(`Allergies: ${p.allergy_details || "see Form 2"}`);
      if (p.cognitive_concerns === "Yes") facts.push(`Cognitive: ${p.cognitive_details || "yes"}`);
      if (p.behavioral_concerns === "Yes") facts.push(`Behavioral: ${p.behavioral_details || "yes"}`);
      if (p.communication_limitations === "Yes") facts.push(`Communication: ${p.communication_details || "yes"}`);
      if (p.weapons_in_home === "Yes") facts.push(`Weapons in home: ${p.weapons_storage || "yes"}`);
      if (p.pets === "Yes") facts.push(`Pets: ${p.pet_details || "yes"}`);
      if (p.smoking_inside === "Yes") facts.push("Someone smokes inside the home");
      if (p.other_safety_concern) facts.push(`Other: ${p.other_safety_concern}`);
      if (p.private_rooms) facts.push(`Off-limits: ${p.private_rooms}`);
      setSafetyFacts(facts);
    }).catch(() => {});
  }, [clientId]);

  const tasks = data.tasks || [];
  const setTask = (code, patch) => onChange({ ...data, tasks: tasks.map((t) => (t.code === code ? { ...t, ...patch } : t)) });
  const toggleLevel = (code, level) => {
    const t = tasks.find((x) => x.code === code);
    const levels = (t.levels || []).includes(level) ? t.levels.filter((l) => l !== level) : [...(t.levels || []), level];
    setTask(code, { levels });
  };
  const isSel = (code) => tasks.find((t) => t.code === code)?.selected;
  const medSel = isSel("118");
  const transportSel = isSel("120");
  const mobilitySel = MOBILITY_CODES.some((c) => isSel(c));

  const setSched = (day, k, v) => {
    const schedule = { ...(data.schedule || {}), [day]: { ...(data.schedule?.[day] || { start: "", end: "" }), [k]: v } };
    onChange({ ...data, schedule, total_weekly_hours: computeTotal(schedule) });
  };

  return (
    <div className="space-y-6" data-testid="form4-form">
      {clientId && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
          <span className="text-sm text-stone-500">Client:</span>
          <code className="rounded-lg border border-stone-200 bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground" data-testid="form4-client-code">{clientCode}</code>
          <a href={`${api.defaults.baseURL}/clients/${clientId}/forms/careplan/pdf`} download
            className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="form4-pdf-button">
            <FilePdf size={14} /> Download PDF
          </a>
        </div>
      )}

      <Section title="Plan Information">
        <Field label="Care plan effective date" tid="f4-effective-date" type="date" value={data.effective_date} onChange={set("effective_date")} />
        <Field label="Authorization start" tid="f4-auth-start" type="date" value={data.auth_start} onChange={set("auth_start")} />
        <Field label="Authorization end" tid="f4-auth-end" type="date" value={data.auth_end} onChange={set("auth_end")} />
        <div className="flex gap-3">
          <div className="flex-1"><Field label="Authorized hours" tid="f4-authorized-hours" value={data.authorized_hours} onChange={set("authorized_hours")} /></div>
          <Radio label="Per" options={["Week", "Month"]} value={data.hours_period} onChange={setR("hours_period")} tid="f4-hours-period" />
        </div>
        <Field label="Assigned supervisor" tid="f4-supervisor" value={data.supervisor} onChange={set("supervisor")} />
        <Field label="Assigned caregiver(s), if known" tid="f4-caregivers" value={data.caregivers} onChange={set("caregivers")} />
      </Section>

      <Section title="Need & Goal" desc="Developed with client/representative input.">
        <div className="md:col-span-2"><Area label="What assistance does the client need from Silver Home Care?" tid="f4-need" value={data.need} onChange={set("need")} /></div>
        <div className="md:col-span-2"><Area label="What does the client want this assistance to help them accomplish or maintain?" tid="f4-goal" value={data.goal} onChange={set("goal")} /></div>
      </Section>

      <Section title="Verbal Intake Summary" desc="During intake, hit record and talk through all the little things that don't fit any field — habits, quirks, family dynamics, house layout, anything. Dictation transcribes it (Tidy cleans it up), and it's included when the AI drafts the care plan packet.">
        <div className="md:col-span-2"><Area label="Verbal summary — everything that didn't fit anywhere else" tid="f4-verbal-summary" value={data.verbal_summary} onChange={set("verbal_summary")} /></div>
      </Section>

      <Section title="Service Schedule">
        <div className="md:col-span-2 rounded-lg border border-primary/25 bg-primary/[0.03] p-4" data-testid="f4-quick-schedule">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Quick fill — set the times once, apply to every day they happen</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Input type="time" value={quick.start} onChange={(e) => setQuick({ ...quick, start: e.target.value })} className="h-8 w-auto border-stone-300 bg-white text-sm" data-testid="f4-quick-start" />
            <span className="text-xs text-stone-400">to</span>
            <Input type="time" value={quick.end} onChange={(e) => setQuick({ ...quick, end: e.target.value })} className="h-8 w-auto border-stone-300 bg-white text-sm" data-testid="f4-quick-end" />
            <label className="ml-2 flex items-center gap-1.5 text-xs text-stone-600" data-testid="f4-quick-has2-label">
              <input type="checkbox" checked={quick.has2} onChange={(e) => setQuick({ ...quick, has2: e.target.checked })} data-testid="f4-quick-has2" />
              second shift
            </label>
            {quick.has2 && (
              <>
                <Input type="time" value={quick.start2} onChange={(e) => setQuick({ ...quick, start2: e.target.value })} className="h-8 w-auto border-stone-300 bg-white text-sm" data-testid="f4-quick-start2" />
                <span className="text-xs text-stone-400">to</span>
                <Input type="time" value={quick.end2} onChange={(e) => setQuick({ ...quick, end2: e.target.value })} className="h-8 w-auto border-stone-300 bg-white text-sm" data-testid="f4-quick-end2" />
              </>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="f4-quick-days">
            <button type="button" onClick={() => setQuick({ ...quick, days: quick.days.length === DAYS.length ? [] : [...DAYS] })}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${quick.days.length === DAYS.length ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 bg-white text-stone-500 hover:border-primary/50"}`}
              data-testid="f4-quick-day-all">Every day</button>
            {DAYS.map((day) => (
              <button key={day} type="button" onClick={() => toggleQuickDay(day)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${quick.days.includes(day) ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 bg-white text-stone-500 hover:border-primary/50"}`}
                data-testid={`f4-quick-day-${day.toLowerCase()}`}>{day.slice(0, 3)}</button>
            ))}
            <Button type="button" size="sm" onClick={applyQuick} disabled={!quick.days.length}
              className="ml-2 bg-primary text-primary-foreground" data-testid="f4-quick-apply">
              Apply to {quick.days.length === DAYS.length ? "all 7 days" : `${quick.days.length || ""} day${quick.days.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
        <div className="md:col-span-2 space-y-2">
          <div className="grid grid-cols-[110px_1fr_1fr_80px] items-center gap-2 text-xs font-medium uppercase tracking-wide text-stone-400">
            <span>Day</span><span>Start</span><span>End</span><span />
          </div>
          {DAYS.map((day) => {
            const ds = data.schedule?.[day] || {};
            const has2 = !!(ds.start2 || ds.end2) || secondDays.has(day);
            return (
              <div key={day} className="space-y-1">
                <div className="grid grid-cols-[110px_1fr_1fr_80px] items-center gap-2">
                  <span className="text-sm text-stone-700">{day}</span>
                  <Input type="time" value={ds.start || ""} onChange={(e) => setSched(day, "start", e.target.value)}
                    className="h-8 border-stone-300 bg-white text-sm" data-testid={`f4-sched-${day.toLowerCase()}-start`} />
                  <Input type="time" value={ds.end || ""} onChange={(e) => setSched(day, "end", e.target.value)}
                    className="h-8 border-stone-300 bg-white text-sm" data-testid={`f4-sched-${day.toLowerCase()}-end`} />
                  {!has2 ? (
                    <button type="button" onClick={() => setSecondDays(new Set([...secondDays, day]))}
                      className="text-[11px] font-medium text-primary hover:underline" data-testid={`f4-sched-${day.toLowerCase()}-add2`}>+ 2nd shift</button>
                  ) : (
                    <button type="button" onClick={() => { setSched(day, "start2", ""); setSched(day, "end2", ""); const n = new Set(secondDays); n.delete(day); setSecondDays(n); }}
                      className="text-[11px] font-medium text-stone-400 hover:text-[#C0392B]" data-testid={`f4-sched-${day.toLowerCase()}-del2`}>remove 2nd</button>
                  )}
                </div>
                {has2 && (
                  <div className="grid grid-cols-[110px_1fr_1fr_80px] items-center gap-2" data-testid={`f4-sched-${day.toLowerCase()}-second`}>
                    <span className="text-[11px] text-stone-400">2nd shift</span>
                    <Input type="time" value={ds.start2 || ""} onChange={(e) => setSched(day, "start2", e.target.value)}
                      className="h-8 border-stone-300 bg-white text-sm" data-testid={`f4-sched-${day.toLowerCase()}-start2`} />
                    <Input type="time" value={ds.end2 || ""} onChange={(e) => setSched(day, "end2", e.target.value)}
                      className="h-8 border-stone-300 bg-white text-sm" data-testid={`f4-sched-${day.toLowerCase()}-end2`} />
                    <span />
                  </div>
                )}
              </div>
            );
          })}
          <p className="pt-1 text-sm text-stone-700">Total weekly hours (calculated): <strong data-testid="f4-total-hours">{data.total_weekly_hours || "—"}</strong></p>
        </div>
        <div className="md:col-span-2"><Area label="Schedule / timing instructions" tid="f4-schedule-instructions" value={data.schedule_instructions} onChange={set("schedule_instructions")} /></div>
      </Section>

      <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm">
        <h3 className="font-heading text-lg font-semibold text-stone-900">Task Codes</h3>
        <p className="mt-0.5 text-xs text-stone-500">Select each task, then set assistance level(s) — multiple allowed — plus frequency and client-specific instructions.</p>
        <div className="mt-4 space-y-2.5">
          {tasks.map((t) => (
            <div key={t.code} className={`rounded-lg border p-4 transition-colors ${t.selected ? "border-primary/40 bg-primary/5" : "border-stone-200 bg-white"}`} data-testid={`f4-task-${t.code}`}>
              <label className="flex cursor-pointer items-center gap-2.5">
                <input type="checkbox" checked={!!t.selected} onChange={(e) => setTask(t.code, { selected: e.target.checked })}
                  className="h-4 w-4 accent-[#2D5A27]" data-testid={`f4-task-${t.code}-select`} />
                <span className="text-sm font-semibold text-stone-900">{t.code} — {t.name}</span>
              </label>
              {t.selected && (
                <div className="mt-3 space-y-2.5 pl-6">
                  <div className="flex flex-wrap gap-1.5">
                    {LEVELS.map((l) => (
                      <button key={l} type="button" onClick={() => toggleLevel(t.code, l)}
                        data-testid={`f4-task-${t.code}-level-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-all ${
                          (t.levels || []).includes(l) ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400 hover:text-stone-800"
                        }`}>{l}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <Input placeholder="Frequency / timing" value={t.frequency} onChange={(e) => setTask(t.code, { frequency: e.target.value })}
                      className="h-8 border-stone-300 bg-white text-sm" data-testid={`f4-task-${t.code}-frequency`} />
                    <Input placeholder="Client-specific instruction or preference" value={t.instruction} onChange={(e) => setTask(t.code, { instruction: e.target.value })}
                      className="h-8 border-stone-300 bg-white text-sm" data-testid={`f4-task-${t.code}-instruction`} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {medSel && (
        <Section title="Managing Medications (118)" desc="Caregivers provide reminders only.">
          <Field label="Medication box / storage location" tid="f4-med-box" value={data.med_box_location} onChange={set("med_box_location")} />
          <Field label="Reminder times" tid="f4-reminder-times" value={data.reminder_times} onChange={set("reminder_times")} />
          <div className="md:col-span-2"><Area label="Medication reminder instructions" tid="f4-reminder-instructions" value={data.reminder_instructions} onChange={set("reminder_instructions")} /></div>
          <div className="md:col-span-2"><Area label="What the caregiver should do if medication is refused / missing / unclear" tid="f4-med-refused" value={data.med_refused_action} onChange={set("med_refused_action")} /></div>
        </Section>
      )}

      {transportSel && (
        <Section title="Transportation (120)">
          <Radio label="Transportation approved?" options={["Yes", "No"]} value={data.transport_approved} onChange={setR("transport_approved")} tid="f4-transport-approved" />
          <Radio label="Vehicle" options={["Client vehicle", "Caregiver vehicle", "Other"]} value={data.transport_vehicle} onChange={setR("transport_vehicle")} tid="f4-transport-vehicle" />
          <div className="md:col-span-2"><Area label="Transportation instructions / restrictions" tid="f4-transport-instructions" value={data.transport_instructions} onChange={set("transport_instructions")} /></div>
        </Section>
      )}

      {mobilitySel && (
        <Section title="Mobility Support (125 / 126 / 128 / 145)">
          <Field label="Equipment used" tid="f4-equipment" value={data.equipment_used} onChange={set("equipment_used")} placeholder="walker, gait belt, transfer board..." />
          <div className="md:col-span-2"><Area label="Transfer / mobility / stair instructions" tid="f4-transfer-instructions" value={data.transfer_instructions} onChange={set("transfer_instructions")} /></div>
        </Section>
      )}

      <Section title="Preferences">
        <div className="md:col-span-2"><Area label="Important routines, preferences, communication needs, or boundaries" tid="f4-routines" value={data.routines_preferences} onChange={set("routines_preferences")} /></div>
        <div className="md:col-span-2"><Area label="Private / off-limits areas or activities" tid="f4-off-limits" value={data.off_limits} onChange={set("off_limits")} /></div>
      </Section>

      <Section title="Safety">
        {safetyFacts.length > 0 && (
          <div className="md:col-span-2 rounded-lg border border-[#D68910]/30 bg-[#D68910]/5 p-4" data-testid="f4-safety-facts">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#9a6a0b]">Carried forward from Form 2 — Intake Assessment</p>
            <ul className="mt-2 space-y-1.5">
              {safetyFacts.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-stone-700">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#D68910]" />{f}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="md:col-span-2"><Area label="Caregiver-specific safety instructions" tid="f4-safety-instructions" value={data.safety_instructions} onChange={set("safety_instructions")} /></div>
      </Section>

      <Section title="Backup Plan">
        <div className="md:col-span-2">
          <CheckGroup label="Backup arrangements" options={BACKUP_OPTIONS} values={data.backup_options} onChange={setR("backup_options")} tidPrefix="f4-backup" />
        </div>
        <div className="md:col-span-2"><Area label="Additional backup instructions" tid="f4-backup-instructions" value={data.backup_instructions} onChange={set("backup_instructions")} /></div>
      </Section>
    </div>
  );
}
